// TournamentContext.tsx (ST-UI) - the always-on tournament context strip: the piece that makes a spot read
// as a TOURNAMENT spot and not a cash hand. Five real engine values in one band above the felt (stack in BB,
// blind level/ante, Harrington M + zone, an ICM-pressure meter, and the pay ladder), built ENTIRELY out of
// the S18/S19 locked tokens (styles/tournament.css) so it matches the cash felt by construction. Every value
// is a real output of the ST1/ST2 engine for this spot - nothing here is placeholder or hand-set.
import type { TournamentContextValues } from '../engine/tournamentRunner';

/** ICM meter marker position (0..100%): risk premium mapped onto the none->severe rail. Clamped so a very
 *  steep bubble pins near the right without running off. */
const meterPct = (riskPremium: number): number =>
  Math.max(6, Math.min(94, (riskPremium / 0.35) * 100));

const PRESSURE_LABEL: Record<string, string> = { none: 'None', moderate: 'Moderate', severe: 'Severe' };
const PRESSURE_CLASS: Record<string, string> = { none: 'none', moderate: 'mod', severe: 'sev' };
const M_ZONE_NOTE: Record<string, string> = {
  green: 'Green zone . play a full game',
  yellow: 'Yellow zone . tighten up',
  orange: 'Orange zone . look to jam',
  red: 'Red zone . jam or fold',
  dead: 'Dead zone . any-two shove',
};

/** Whole-dollar buy-in parsed from the display label ("$100" -> 100), for the pay-ladder pool estimate. */
const buyInDollars = (label: string): number => Number(label.replace(/[^0-9.]/g, '')) || 0;

/** English ordinal for a place ("21st", "10th", "13th") - correct across the 11-13 exception and every
 *  last digit, so a two-digit field size (TF1's running field) reads right, not "21th". */
const ordinal = (n: number): string => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

interface Rung {
  place: string;
  dollars: string;
  kind?: 'bubble' | 'next' | 'top';
}

/** The compact pay ladder: the bubble (busts for $0), the min-cash (the next pay jump), the rung below it,
 *  and the top prize. Dollar values are the engine's payout FRACTIONS applied to an estimated pool
 *  (buy-in x field) - the fractions are the un-hallucinatable truth; the pool is a display scale. */
const buildRungs = (c: TournamentContextValues): Rung[] => {
  const pool = buyInDollars(c.buyInLabel) * c.fieldSize;
  const dollarsAt = (place: number): string => {
    const frac = c.payoutsInPlay[place - 1];
    return frac === undefined ? '-' : `$${Math.round(frac * pool).toLocaleString()}`;
  };
  const minCash = c.paidPlaces;
  return [
    { place: ordinal(c.playersLeft), dollars: '$0', kind: 'bubble' },
    { place: ordinal(minCash), dollars: dollarsAt(minCash), kind: 'next' },
    { place: ordinal(minCash - 1), dollars: dollarsAt(minCash - 1) },
    { place: '...', dollars: '' },
    { place: '1st', dollars: dollarsAt(1), kind: 'top' },
  ];
};

export function TournamentContext({ c }: { c: TournamentContextValues }) {
  return (
    <>
      <div className="tctx">
        <div className="tctx-top">
          <div className="tcell">
            <span className="lab">Your stack</span>
            <span className="val num">
              {c.effectiveStackBB.toFixed(1)}
              <small>&nbsp;BB</small>
            </span>
            <span className="sub num">{c.heroChips.toLocaleString()} chips</span>
          </div>
          <div className="tcell">
            <span className="lab">Blinds . L{c.levelNumber}</span>
            <span className="val num">
              {c.sb.toLocaleString()} / {c.bb.toLocaleString()}
            </span>
            <span className="sub num">{c.ante > 0 ? `${c.ante.toLocaleString()} ante` : 'no ante'}</span>
          </div>
          <div className={'tcell mzone mz-' + c.mZone}>
            <span className="lab">M-ratio</span>
            <span className="val num">
              <span className="dot" />
              &nbsp;{c.m.toFixed(1)}
            </span>
            <span className="sub">{M_ZONE_NOTE[c.mZone]}</span>
          </div>
        </div>

        <div className="tctx-icm">
          <div className="icm-head">
            <span className="lab">ICM pressure</span>
            <span className={'state ' + (PRESSURE_CLASS[c.pressure] ?? 'sev')}>
              {PRESSURE_LABEL[c.pressure] ?? c.pressure}
            </span>
          </div>
          <div className="icm-rail" aria-hidden="true">
            <span className="mark" style={{ left: `${meterPct(c.riskPremium)}%` }} />
          </div>
          <div className="icm-tax">
            <div className="big num">+{Math.round(c.riskPremium * 100)}%</div>
            <div className="lil">risk premium</div>
          </div>
        </div>
      </div>

      <div className="ladder">
        <div className="ladder-h">
          <span className="t">Pay ladder</span>
          <span className="m num">
            {c.playersLeft} left . {c.paidPlaces} paid . you are {ordinal(c.heroStackRank)} in chips
          </span>
        </div>
        <div className="rungs">
          {buildRungs(c).map((r, i) => (
            <div key={i} className={'rung' + (r.kind ? ' ' + r.kind : '')}>
              <div className="p">{r.place}</div>
              <div className="d num">{r.dollars || ' '}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
